// client/src/lib/analytics.ts
// GA4 (gtag) + Microsoft Clarity — env-driven, browser-only.
//
// COMPLETE NO-OP when the IDs are unset, so local dev and PRs never pollute
// analytics. Only PUBLIC ids live here (GA4 Measurement ID, Clarity project id);
// the GA4 Measurement Protocol secret is backend-only and is never imported here.
//
// Contract: every exported function is safe to call anytime (before/after init,
// enabled or not) and must NEVER throw into the app.

const GA4_ID = (process.env.REACT_APP_GA4_ID || '').trim();
const CLARITY_ID = (process.env.REACT_APP_CLARITY_ID || '').trim();
const ENV = (process.env.REACT_APP_ANALYTICS_ENV || '').trim() || 'unknown';
const APP_VERSION = (process.env.REACT_APP_VERSION || '').trim() || 'dev';

const gaEnabled = GA4_ID.length > 0;
const clarityEnabled = CLARITY_ID.length > 0;

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    clarity?: (...args: any[]) => void;
  }
}

let started = false;
let userRole = 'anonymous';

function loadGtag(): void {
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  // We emit our own page_view per route change (clean screen names), so turn off
  // gtag's automatic page_view to avoid double counting.
  window.gtag('config', GA4_ID, { send_page_view: false });
}

function loadClarity(): void {
  // Standard Microsoft Clarity loader, parameterized by the project id.
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] = c[a] || function () {
      (c[a].q = c[a].q || []).push(arguments);
    };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode!.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_ID);
}

/** Load the GA4 + Clarity scripts once. No-op if the ids are unset. */
export function initAnalytics(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  if (gaEnabled) { try { loadGtag(); } catch { /* ignore */ } }
  if (clarityEnabled) { try { loadClarity(); } catch { /* ignore */ } }
}

function baseParams(): Record<string, any> {
  return { env: ENV, app_version: APP_VERSION, user_role: userRole };
}

/** Fire a GA4 event with the common context params merged in. No-op if disabled. */
export function track(name: string, params: Record<string, any> = {}): void {
  if (!gaEnabled || !window.gtag) return;
  try {
    window.gtag('event', name, { ...baseParams(), ...params });
  } catch { /* never break the app for analytics */ }
}

/** Tag the session with the user's role (+ opaque id). Never sends PII (no email/name). */
export function identifyUser(role?: string | null, id?: string | null): void {
  userRole = (role || 'anonymous').toString();
  if (gaEnabled && window.gtag) {
    try {
      window.gtag('set', 'user_properties', { user_role: userRole });
      if (id) window.gtag('config', GA4_ID, { user_id: String(id) });
    } catch { /* ignore */ }
  }
  if (clarityEnabled && window.clarity) {
    try {
      if (id) window.clarity('identify', String(id));
      window.clarity('set', 'role', userRole);
      window.clarity('set', 'env', ENV);
    } catch { /* ignore */ }
  }
}

export function clearUser(): void {
  userRole = 'anonymous';
}

/** Record a screen/page view with a clean, ID-stripped screen name. */
export function setScreen(name: string, path?: string): void {
  if (gaEnabled && window.gtag) {
    try {
      window.gtag('event', 'page_view', {
        ...baseParams(),
        page_title: name,
        page_path: path,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        screen_name: name,
      });
    } catch { /* ignore */ }
  }
  if (clarityEnabled && window.clarity) {
    try { window.clarity('set', 'screen', name); } catch { /* ignore */ }
  }
}

/** Web-Vitals reporter — wire into reportWebVitals(trackWebVital). */
export function trackWebVital(metric: { name: string; value: number; rating?: string }): void {
  // CLS is a unitless ratio; scale x1000 so GA4 shows a usable integer.
  const value = Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value);
  track('web_vitals', { metric: metric.name, value, rating: metric.rating });
}

export const analyticsStatus = { ga: gaEnabled, clarity: clarityEnabled };
